import { memo, useEffect, useMemo, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
} from "@mui/material";

const DEFAULT_ROWS_PER_PAGE_OPTIONS = [25, 50, 100, 250];

const PaginatedDataTable = memo(function PaginatedDataTable({
  columns = [],
  rows = [],
  getRowKey,
  showSerialNo = true,
  serialNoHeader = "Sr.No",
  serialNoAlign = "center",
  maxHeight = 440,
  emptyMessage = "No records found.",
  rowsPerPageOptions = DEFAULT_ROWS_PER_PAGE_OPTIONS,
  defaultRowsPerPage = 50,
  resetKey = "",
  stickyHeader = true,
  tableAriaLabel = "data table",
  headerCellSx = { color: "#000" },
}) {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(defaultRowsPerPage);

  useEffect(() => {
    setPage(0);
  }, [resetKey, rows.length]);

  const paginatedRows = useMemo(() => {
    const start = page * rowsPerPage;
    return rows.slice(start, start + rowsPerPage);
  }, [rows, page, rowsPerPage]);

  const serialNoOffset = page * rowsPerPage;

  const handleChangePage = (_event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const totalColumns = columns.length + (showSerialNo ? 1 : 0);

  return (
    <>
      <TableContainer sx={{ maxHeight }}>
        <Table stickyHeader={stickyHeader} aria-label={tableAriaLabel}>
          <TableHead>
            <TableRow>
              {showSerialNo && (
                <TableCell
                  align={serialNoAlign}
                  sx={{
                    ...headerCellSx,
                    minWidth: 72,
                    width: 72,
                  }}
                >
                  {serialNoHeader}
                </TableCell>
              )}
              {columns.map((column) => (
                <TableCell
                  key={column.key}
                  align={column.align || "center"}
                  sx={{
                    ...headerCellSx,
                    ...(column.width ? { minWidth: column.width, width: column.width } : {}),
                  }}
                >
                  {column.header}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          <TableBody>
            {paginatedRows.length > 0 ? (
              paginatedRows.map((row, rowIndex) => {
                const serialNo = serialNoOffset + rowIndex + 1;
                const rowKey = getRowKey
                  ? getRowKey(row, serialNoOffset + rowIndex)
                  : row?.id ?? serialNoOffset + rowIndex;

                return (
                  <TableRow key={rowKey}>
                    {showSerialNo && (
                      <TableCell align={serialNoAlign} sx={{ minWidth: 72, width: 72 }}>
                        {serialNo}
                      </TableCell>
                    )}
                    {columns.map((column) => (
                      <TableCell
                        key={column.key}
                        align={column.align || "center"}
                        sx={column.cellSx}
                      >
                        {column.render
                          ? column.render(row, serialNo)
                          : row?.[column.key] ?? "-"}
                      </TableCell>
                    ))}
                  </TableRow>
                );
              })
            ) : (
              <TableRow>
                <TableCell colSpan={totalColumns} align="center">
                  {emptyMessage}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {rows.length > 0 && (
        <TablePagination
          component="div"
          count={rows.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={rowsPerPageOptions}
          labelDisplayedRows={({ from, to, count }) =>
            `${from}-${to} of ${count !== -1 ? count : `more than ${to}`}`
          }
        />
      )}
    </>
  );
});

export default PaginatedDataTable;
